import { useState } from 'react';
import { IonContent, IonPage } from '@ionic/react';
import { useParams, useHistory, Redirect } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth/useAuth';
import { getPublication, getMyPublications } from '@/lib/api/publications';
import { getDocumentsByPublication, createDocument, moveDocumentToPublication } from '@/lib/api/documents';
import { Logo } from '@/components/ui/Logo';

const KIND_LABEL: Record<string, string> = {
  article: 'Article', essay: 'Essay', book: 'Book', series: 'Series',
};

const VISIBILITY_STYLES: Record<string, string> = {
  public: 'bg-green-50 text-green-700',
  unlisted: 'bg-yellow-50 text-yellow-700',
  private: 'bg-gray-100 text-gray-500',
};

export default function PublicationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { status } = useAuth();
  const history = useHistory();
  const queryClient = useQueryClient();
  const [newTitle, setNewTitle] = useState('');
  const [showNewForm, setShowNewForm] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  // id of the document currently showing the "move to" dropdown, or null
  const [movingDocId, setMovingDocId] = useState<string | null>(null);

  const { data: pub, isLoading: pubLoading, isError: pubError } = useQuery({
    queryKey: ['publication', id],
    queryFn: () => getPublication(id),
  });

  const { data: documents = [], isLoading: docsLoading } = useQuery({
    queryKey: ['documents', id],
    queryFn: () => getDocumentsByPublication(id),
    enabled: !!pub,
  });

  const createMutation = useMutation({
    mutationFn: ({ title }: { title: string }) => createDocument(id, title),
    onSuccess: ({ documentId }) => {
      queryClient.invalidateQueries({ queryKey: ['documents', id] });
      setNewTitle('');
      setShowNewForm(false);
      history.push(`/edit/${documentId}`);
    },
    onError: (err: unknown) => {
      const pgErr = err as { message?: string };
      setFormError(pgErr?.message ?? 'Something went wrong.');
    },
  });

  const moveMutation = useMutation({
    mutationFn: ({ docId, pubId }: { docId: string; pubId: string }) =>
      moveDocumentToPublication(docId, pubId),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['documents', id] });
      queryClient.invalidateQueries({ queryKey: ['documents', vars.pubId] });
      setMovingDocId(null);
    },
  });

  const { data: allPublications = [] } = useQuery({
    queryKey: ['my-publications'],
    queryFn: getMyPublications,
    enabled: status === 'authenticated',
  });

  if (status === 'unauthenticated') return <Redirect to="/auth" />;
  if (status === 'needs-handle') return <Redirect to="/auth/claim-handle" />;

  // Show owner controls to any authenticated user — RLS rejects unauthorized actions.
  // The strict pub.author_id check was hiding the button when profile hadn't loaded.
  const isOwner = status === 'authenticated';

  function submitNewDoc(e: React.FormEvent) {
    e.preventDefault();
    if (!newTitle.trim()) return;
    setFormError(null);
    createMutation.mutate({ title: newTitle.trim() });
  }

  return (
    <IonPage>
      <IonContent scrollY fullscreen>
        <header className="sticky top-0 z-10 border-b border-gray-200 bg-white">
          <div className="mx-auto flex max-w-3xl items-center gap-3 px-5 py-3">
            <button
              onClick={() => history.goBack()}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
              aria-label="Back"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 18l-6-6 6-6"/>
              </svg>
            </button>
            <Logo size="sm" />
          </div>
        </header>

        <main className="mx-auto max-w-3xl px-5 py-8 pb-20">
          {pubLoading && (
            <div className="flex justify-center py-20">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-[#2f3e46]" />
            </div>
          )}

          {pubError && (
            <p className="py-20 text-center text-gray-500">Publication not found.</p>
          )}

          {pub && (
            <>
              {/* Publication header */}
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-[0.67rem] font-extrabold uppercase tracking-widest text-[#1a56a0]">
                      {KIND_LABEL[pub.kind] ?? pub.kind}
                    </span>
                    <span className={`rounded-full px-2 py-0.5 text-[0.65rem] font-medium ${VISIBILITY_STYLES[pub.visibility] ?? 'bg-gray-100 text-gray-500'}`}>
                      {pub.visibility}
                    </span>
                  </div>
                  <h1 className="font-serif text-2xl font-bold text-gray-900">{pub.title}</h1>
                  <p className="mt-1 font-mono text-xs text-gray-400">/{pub.slug}</p>
                </div>
                {isOwner && (
                  <button
                    onClick={() => history.push(`/publications/${pub.id}/edit`)}
                    className="shrink-0 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                  >
                    Edit
                  </button>
                )}
              </div>

              {/* Documents section */}
              <div className="mt-10">
                <div className="mb-5 flex items-center justify-between">
                  <h2 className="text-xs font-extrabold uppercase tracking-widest text-gray-400">
                    Documents
                  </h2>
                  {isOwner && !showNewForm && (
                    <button
                      onClick={() => setShowNewForm(true)}
                      className="rounded-lg bg-[#2f3e46] px-4 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-[#1e2c33]"
                    >
                      New document
                    </button>
                  )}
                </div>

                {/* New document form */}
                {showNewForm && (
                  <form onSubmit={submitNewDoc} className="mb-6 flex flex-col gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <label className="text-sm font-medium text-gray-700" htmlFor="new-doc-title">
                      Document title
                    </label>
                    <input
                      id="new-doc-title"
                      type="text"
                      value={newTitle}
                      onChange={e => setNewTitle(e.target.value)}
                      maxLength={200}
                      placeholder="Untitled"
                      autoFocus
                      className="rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-[#1a56a0] focus:ring-1 focus:ring-[#1a56a0]"
                    />
                    {formError && (
                      <p className="text-xs text-red-500">{formError}</p>
                    )}
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => { setShowNewForm(false); setNewTitle(''); setFormError(null); }}
                        className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={createMutation.isPending || !newTitle.trim()}
                        className="rounded-lg bg-[#2f3e46] px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#1e2c33] disabled:opacity-50"
                      >
                        {createMutation.isPending ? 'Creating…' : 'Create'}
                      </button>
                    </div>
                  </form>
                )}

                {docsLoading && (
                  <div className="flex justify-center py-8">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-[#2f3e46]" />
                  </div>
                )}

                {!docsLoading && documents.length === 0 && !showNewForm && (
                  <p className="py-8 text-center text-sm text-gray-400">
                    {isOwner ? 'No documents yet. Create the first one.' : 'No documents published yet.'}
                  </p>
                )}

                <div className="flex flex-col divide-y divide-gray-100">
                  {documents.map(doc => (
                    <div key={doc.id} className="flex items-center justify-between gap-4 py-4">
                      <div
                        className="group flex min-w-0 flex-1 cursor-pointer flex-col gap-0.5"
                        onClick={() => history.push(`/read/${doc.id}`)}
                      >
                        <h3 className="font-serif text-base font-bold leading-snug text-gray-900 transition-colors group-hover:text-[#1a56a0]">
                          {doc.title}
                        </h3>
                        <p className="text-xs text-gray-400">
                          {doc.current_version_id ? (
                            <span className="font-medium text-green-600">Published</span>
                          ) : (
                            <span className="font-medium text-amber-500">Draft</span>
                          )}
                          &nbsp;·&nbsp;
                          {new Date(doc.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                      </div>
                      {isOwner && (
                        <div className="flex shrink-0 items-center gap-2">
                          {movingDocId === doc.id ? (
                            <>
                              <select
                                autoFocus
                                defaultValue={id}
                                disabled={moveMutation.isPending}
                                onChange={e => moveMutation.mutate({ docId: doc.id, pubId: e.target.value })}
                                className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-700 outline-none focus:border-[#1a56a0]"
                              >
                                {allPublications.map(p => (
                                  <option key={p.id} value={p.id}>{p.title}</option>
                                ))}
                              </select>
                              <button
                                onClick={() => setMovingDocId(null)}
                                className="text-gray-400 hover:text-gray-600"
                                aria-label="Cancel move"
                              >
                                ✕
                              </button>
                            </>
                          ) : (
                            <>
                              {allPublications.length > 1 && (
                                <button
                                  onClick={() => setMovingDocId(doc.id)}
                                  className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-500 transition-colors hover:bg-gray-50"
                                >
                                  Move to…
                                </button>
                              )}
                              <button
                                onClick={() => history.push(`/edit/${doc.id}`)}
                                className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50"
                              >
                                Edit
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </main>
      </IonContent>
    </IonPage>
  );
}
